import { useRef, useState } from "react"
import { useIntl } from "react-intl"

import classNames from "classnames"
import { Form, Formik } from "formik"

import {
  Button,
  GlobalSnackbarRegion,
  ICONS,
  StaffBodyTextM,
  StaffBodyTextXS,
  StaffHeading1
} from "@doktor-se/bones-ui"
import { Loader } from "@doktor-se/bones-ui/dist/web-shared/components"

import { uploadClinicFile } from "api"
import { useAppDispatch } from "lib/hooks"

import PageContainer from "components/PageContainer/PageContainer"

import styles from "./UploadPage.module.scss"

const UploadPage = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = (file: File, resetForm: () => void) => {
    setIsFileUploading(true)
    dispatch(uploadClinicFile(file))
      .then(
        () => {
          resetForm()
          setFileName("")
          GlobalSnackbarRegion.queue.add(
            {
              text: intl.formatMessage({ id: "upload.file.upload.successful" }),
              variant: "success"
            },
            { timeout: 5000 }
          )
        },
        () => {
          setFormError(intl.formatMessage({ id: "upload.body.text" }))
        }
      )
      .finally(() => setIsFileUploading(false))
  }

  const [fileName, setFileName] = useState<string>("")
  const [formError, setFormError] = useState<string>("")
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false)

  return (
    <PageContainer>
      <StaffHeading1 margin={{ bottom: "30px" }}>{intl.formatMessage({ id: "label.upload" })}</StaffHeading1>
      <StaffBodyTextM>{intl.formatMessage({ id: "upload.body.text" })}</StaffBodyTextM>
      <Formik
        initialValues={{ file: null }}
        onSubmit={(values, { resetForm }) => {
          if (!values.file) {
            setFormError(intl.formatMessage({ id: "upload.file.error.choose.file" }))
            return
          }

          handleFileUpload(values.file, resetForm)
        }}>
        {({ setFieldValue }) => (
          <div className={styles["file-upload-container"]}>
            <Form>
              <div className={styles["file-upload-body"]}>
                <div className={classNames(styles["file-upload-wrapper"], { [styles["error-border"]]: formError })}>
                  <input
                    type="file"
                    id="fileInput"
                    className={styles["file-input"]}
                    ref={fileInputRef}
                    onChange={event => {
                      const file = event.currentTarget.files?.[0]
                      setFileName(file ? file.name : "")
                      setFieldValue("file", file)
                      setFormError("")
                    }}
                  />
                  <label htmlFor="fileInput" className={styles["custom-file-button"]}>
                    Choose file
                  </label>
                  <hr className={styles["file-divider"]} />
                  {isFileUploading ? (
                    <Loader type="small" />
                  ) : (
                    <div className={styles["file-name"]}>
                      <span className={fileName ? "" : styles["no-file"]}>
                        {fileName || intl.formatMessage({ id: "upload.file.no.file" })}
                      </span>
                      {fileName && (
                        <ICONS.Delete
                          type="button"
                          onClick={() => {
                            setFileName("")
                            setFieldValue("file", null)
                            setFormError("")
                            if (fileInputRef.current) {
                              fileInputRef.current.value = ""
                            }
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
                {formError && <StaffBodyTextXS className={styles["error-message"]}>{formError}</StaffBodyTextXS>}
              </div>
              <Button variant="primary" type="submit" color="primary">
                {intl.formatMessage({ id: "upload.btn.upload.text" })}
              </Button>
            </Form>
          </div>
        )}
      </Formik>
    </PageContainer>
  )
}

export default UploadPage
